import React, { useState, useEffect } from 'react';
import './UserEndpointManager.css';
import { endpointAPI } from '../../../../services';
import { setConfigEndpoint, getConfigEndpoint, storeEndpointsList } from '../../../../services/endpoints/configEndpointManager';
import { getAllEndpoints } from '../../../../services/endpoints/endpointStore';

const PublicDNSManager = () => {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddEndpointDialog, setShowAddEndpointDialog] = useState(false);
  const [showEditEndpointDialog, setShowEditEndpointDialog] = useState(false);
  const [currentEndpoint, setCurrentEndpoint] = useState(null);

  // Update config service endpoint when endpoints change
  const updateConfigEndpoint = (endpointList) => {
    if (endpointList && endpointList.length > 0) {
      // Store endpoints list for persistence
      storeEndpointsList(endpointList);
      
      // Get the first active endpoint
      const firstActiveEndpoint = endpointList.find(e => e.status === 'active');
      if (firstActiveEndpoint) {
        const baseUrl = firstActiveEndpoint.baseUrl || firstActiveEndpoint.url;
        if (baseUrl && baseUrl !== getConfigEndpoint()) {
          console.log('[PublicDNSManager] Setting config service endpoint to:', baseUrl);
          setConfigEndpoint(baseUrl);
        }
      }
    }
  };

  useEffect(() => {
    fetchEndpoints();
  }, []);

  // Always initialize config endpoint from localStorage on mount
  useEffect(() => {
    const storedEndpoint = getConfigEndpoint();
    if (storedEndpoint) {
      console.log('[PublicDNSManager] Config endpoint already initialized:', storedEndpoint);
    } else {
      console.log('[PublicDNSManager] ⚠️ No config endpoint found in localStorage');
    }
  }, []);

  const fetchEndpoints = async () => {
    try {
      setLoading(true);
      setEndpoints([]);

      // Use cached endpoints instead of making API call
      const cachedEndpoints = getAllEndpoints();

      if (cachedEndpoints && cachedEndpoints.length > 0) {
        const formattedEndpoints = cachedEndpoints.map(endpoint => ({
          id: endpoint._id,
          name: endpoint.name || 'Unnamed Endpoint',
          baseUrl: endpoint.baseUrl || endpoint.url || '',
          url: endpoint.baseUrl || endpoint.url || '',
          status: endpoint.isActive ? 'active' : 'inactive',
          createdAt: endpoint.createdAt
        }));

        setEndpoints(formattedEndpoints);
        updateConfigEndpoint(formattedEndpoints);
        console.log('[PublicDNSManager] Using cached endpoints:', formattedEndpoints.length, 'endpoints');
      } else {
        // If no cached endpoints, try fetching from API
        console.log('[PublicDNSManager] No cached endpoints, fetching from API...');
        const response = await endpointAPI.getAll();

        if (response && response.data) {
          const endpointData = typeof response.data === 'string'
            ? JSON.parse(response.data)
            : response.data;

          if (Array.isArray(endpointData)) {
            const formattedEndpoints = endpointData.map(endpoint => ({
              id: endpoint._id,
              name: endpoint.name || 'Unnamed Endpoint',
              baseUrl: endpoint.baseUrl || endpoint.url || '',
              url: endpoint.baseUrl || endpoint.url || '',
              status: endpoint.isActive ? 'active' : 'inactive',
              createdAt: endpoint.createdAt
            }));

            setEndpoints(formattedEndpoints);
            updateConfigEndpoint(formattedEndpoints);
          } else {
            setEndpoints([]);
            setError('Invalid endpoint data format: expected an array');
          }
        } else {
          setEndpoints([]);
          setError('Invalid response format when fetching endpoints');
        }
      }
    } catch (err) {
      setError('Failed to fetch endpoints: ' + (err.message || 'Unknown error'));
      console.error('Error fetching endpoints:', err);
      setEndpoints([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenAddEndpointDialog = () => {
    setShowAddEndpointDialog(true);
  };
  
  const handleCloseAddEndpointDialog = () => {
    setShowAddEndpointDialog(false);
  };
  
  const handleOpenEditEndpointDialog = (endpoint) => {
    setCurrentEndpoint(endpoint);
    setShowEditEndpointDialog(true);
  };
  
  const handleCloseEditEndpointDialog = () => {
    setShowEditEndpointDialog(false);
    setCurrentEndpoint(null);
  };
  
  // Add/Edit handlers
  const handleAddEndpoint = async (formData) => {
    try {
      const response = await endpointAPI.create({
        baseUrl: formData.baseUrl.trim(),
        name: formData.name.trim()
      });
      
      if (response && (response.status === 200 || response.status === 201)) {
        console.log('Endpoint added successfully');
        setError(null);
        alert('Endpoint added successfully');
      } else {
        throw new Error('Failed to add endpoint');
      }
      
      await fetchEndpoints();
      handleCloseAddEndpointDialog();
    } catch (err) {
      setError('Failed to add endpoint: ' + (err.message || 'Unknown error'));
      console.error('Error adding endpoint:', err);
    }
  };
  
  const handleEditEndpoint = async (formData) => {
    try {
      if (currentEndpoint && currentEndpoint.id) {
        const response = await endpointAPI.update(currentEndpoint.id, {
          baseUrl: formData.baseUrl.trim(),
          name: formData.name.trim()
        });
        
        if (response && (response.status === 200 || response.status === 201)) {
          console.log('Endpoint updated successfully');
          setError(null);
        } else {
          throw new Error('Failed to update endpoint');
        }
      } else {
        throw new Error('Current endpoint ID not found');
      }
      
      await fetchEndpoints();
      handleCloseEditEndpointDialog();
    } catch (err) {
      setError('Failed to update endpoint: ' + (err.message || 'Unknown error'));
      console.error('Error updating endpoint:', err);
    }
  };
  
  const handleDeleteEndpoint = async (endpointId) => {
    if (window.confirm('Are you sure you want to delete this endpoint?')) {
      try {
        const response = await endpointAPI.delete(endpointId);
        
        if (response && response.status === 200) {
          console.log('Endpoint deleted successfully');
        } else {
          throw new Error('Failed to delete endpoint');
        }
        
        await fetchEndpoints();
      } catch (err) {
        setError('Failed to delete endpoint: ' + (err.message || 'Unknown error'));
        console.error('Error deleting endpoint:', err);
      }
    }
  };
  
  const handleToggleEndpointStatus = async (endpointId, currentStatus) => {
    try {
      const endpoint = endpoints.find(e => e.id === endpointId);
      if (!endpoint) {
        throw new Error('Endpoint not found');
      }
      
      // Toggle the active status
      const newActiveStatus = currentStatus !== 'active';
      
      const response = await endpointAPI.update(endpointId, {
        baseUrl: endpoint.baseUrl || endpoint.url,
        name: endpoint.name,
        isActive: newActiveStatus
      });
      
      if (response && response.status === 200) {
        console.log(`Endpoint status updated to ${newActiveStatus ? 'active' : 'inactive'}`);
      } else {
        throw new Error('Failed to update endpoint status');
      }
      
      await fetchEndpoints();
    } catch (err) {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const updatedEndpoints = endpoints.map(endpoint => 
        endpoint.id === endpointId ? { ...endpoint, status: newStatus } : endpoint
      );
      
      setEndpoints(updatedEndpoints);
      setError('Failed to update endpoint status: ' + (err.message || 'Unknown error'));
      console.error('Error updating endpoint status:', err);
    }
  };
  
  const dismissError = () => {
    setError(null);
  };
  
  // Helper function to format dates
    const formatDate = (dateString) => {
      if (!dateString) return 'Unknown';
      
      try {
        const date = new Date(dateString);
        return date.toLocaleString();
      } catch (e) {
        return dateString;
      }
    };
    
  // Render functions
  const renderEndpointsTable = () => {
    if (loading) {
      return (
        <div className="loading-container" style={{ padding: '2rem', textAlign: 'center' }}>
          <div className="loading-spinner"></div>
          <p>Loading endpoints...</p>
        </div>
      );
    }
    
    return (
      <table className="user-endpoint-table">
        <thead>
          <tr>
            <th>Service Name</th>
            <th>DNS Address</th>
            <th>Status</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {endpoints.length === 0 ? (
            <tr>
              <td colSpan="5" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
                <div className="empty-state">
                  <i className="fas fa-globe" style={{ fontSize: '2rem', marginBottom: '1rem', opacity: 0.5 }}></i>
                  <p>No DNS entries found. Add a new DNS entry to allow frontend access to backend services.</p>
                </div>
              </td>
            </tr>
          ) : (
            endpoints.map((endpoint, index) => (
              <tr key={endpoint.id || endpoint.baseUrl || `endpoint-${index}`}>
                <td>{endpoint.name}</td>
                <td title={endpoint.baseUrl || endpoint.url}>
                  <code className="endpoint-url">
                    {endpoint.baseUrl || endpoint.url}
                  </code>
                </td>
                <td>
                  <span className={`user-endpoint-status status-${endpoint.status}`}>
                    <i className={`fas ${endpoint.status === 'active' ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                    {endpoint.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {formatDate(endpoint.createdAt)}
                </td>
                <td className="user-endpoint-actions">
                  <button 
                    className="action-btn action-btn-edit" 
                    onClick={() => handleOpenEditEndpointDialog(endpoint)}
                    title="Edit DNS Entry"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button 
                    className="action-btn" 
                    onClick={() => handleToggleEndpointStatus(endpoint.id, endpoint.status)}
                    title={endpoint.status === 'active' ? 'Deactivate DNS Entry' : 'Activate DNS Entry'}
                  >
                    <i className={`fas ${endpoint.status === 'active' ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                  </button>
                  <button 
                    className="action-btn action-btn-delete" 
                    onClick={() => handleDeleteEndpoint(endpoint.id)}
                    title="Delete DNS Entry"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  };
  
  // Dialog components
  const AddEndpointDialog = ({ show, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
      name: '',
      baseUrl: ''
    });
    
    const handleChange = (e) => {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };
    
    if (!show) return null;
    
    return (
      <div className="ue-modal">
        <div className="ue-modal-content">
          <div className="ue-modal-header">
            <h2>Add New DNS Entry</h2>
            <button type="button" className="close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="ue-modal-body">
              <div className="ue-modal-info">
                <div className="ue-modal-info-title">
                  <i className="fas fa-info-circle"></i>
                  About DNS Entries
                </div>
                <p>
                  These DNS entries define the public addresses that frontend applications use to connect to backend services. 
                  Each entry maps a service name to its accessible domain address.
                </p>
              </div>
              <div className="ue-form-group">
                <label htmlFor="name">Service Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter service name (e.g., Trading API, Auth Service)"
                  required
                />
                <small style={{display: "block", marginTop: "5px", color: "#666"}}>
                  A descriptive name for the backend service
                </small>
              </div>
              <div className="ue-form-group">
                <label htmlFor="baseUrl">DNS Address *</label>
                <input
                  type="text"
                  id="baseUrl"
                  name="baseUrl"
                  value={formData.baseUrl}
                  onChange={handleChange}
                  placeholder="api.example.com"
                  required
                />
                <small style={{display: "block", marginTop: "5px", color: "#666"}}>
                  The public domain/address that frontend apps will use to reach this service (without protocol)
                </small>
              </div>
            </div>
            <div className="ue-modal-footer">
              <button
                type="button"
                className="ue-btn ue-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="ue-btn ue-btn-primary">
                <i className="fas fa-plus" style={{ marginRight: '0.5rem' }}></i>
                Add DNS Entry
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  const EditEndpointDialog = ({ show, endpoint, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
      name: '',
      baseUrl: ''
    });
    
    useEffect(() => {
      if (endpoint) {
        setFormData({
          name: endpoint.name || '',
          baseUrl: endpoint.baseUrl || endpoint.url || ''
        });
      }
    }, [endpoint]);
    
    const handleChange = (e) => {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };
    
    if (!show) return null;
    
    return (
      <div className="ue-modal">
        <div className="ue-modal-content">
          <div className="ue-modal-header">
            <h2>Edit DNS Entry</h2>
            <button type="button" className="close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="ue-modal-body">
              <div className="ue-modal-info">
                <div className="ue-modal-info-title">
                  <i className="fas fa-info-circle"></i>
                  About DNS Entries
                </div>
                <p>
                  Update the DNS entry that frontend applications use to connect to this backend service.
                </p>
              </div>
              <div className="ue-form-group">
                <label htmlFor="name">Service Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter service name (e.g., Trading API, Auth Service)"
                  required
                />
                <small style={{display: "block", marginTop: "5px", color: "#666"}}>
                  A descriptive name for the backend service
                </small>
              </div>
              <div className="ue-form-group">
                <label htmlFor="baseUrl">DNS Address *</label>
                <input
                  type="text"
                  id="baseUrl"
                  name="baseUrl"
                  value={formData.baseUrl}
                  onChange={handleChange}
                  placeholder="api.example.com"
                  required
                />
                <small style={{display: "block", marginTop: "5px", color: "#666"}}>
                  The public domain/address that frontend apps will use to reach this service (without protocol)
                </small>
              </div>
            </div>
            <div className="ue-modal-footer">
              <button
                type="button"
                className="ue-btn ue-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="ue-btn ue-btn-primary">
                <i className="fas fa-save" style={{ marginRight: '0.5rem' }}></i>
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  return (
    <div className="user-endpoint-manager">
      <div className="content-header">
        <h2>Public DNS Manager</h2>
        <p>Manage public DNS entries that frontend applications use to access backend services</p>
      </div>

      {error && (
        <div className="ue-error-notice">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
          <button className="close-btn" onClick={dismissError}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="user-endpoint-content">
        <div className="user-endpoint-header">
          <h3>Public DNS Management</h3>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
            <button
              className="ue-btn ue-btn-primary"
              onClick={handleOpenAddEndpointDialog}
            >
              <i className="fas fa-plus" style={{ marginRight: '0.5rem' }}></i>
              Add DNS Entry
            </button>
            <small className="hint-text">
              These DNS entries allow frontend applications to discover and connect to backend services
            </small>
          </div>
        </div>
        {renderEndpointsTable()}
      </div>
      
      <AddEndpointDialog 
        show={showAddEndpointDialog}
        onClose={handleCloseAddEndpointDialog}
        onSubmit={handleAddEndpoint}
      />
      
      <EditEndpointDialog 
        show={showEditEndpointDialog}
        endpoint={currentEndpoint}
        onClose={handleCloseEditEndpointDialog}
        onSubmit={handleEditEndpoint}
      />
    </div>
  );
};

export default PublicDNSManager;